import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import {
  Card,
  Row,
  Col,
  Badge,
  Alert,
  Button,
  Form,
  IconButton,
  ActionRow,
  Dropdown,
  ModalDialog,
  useToggle,
} from '@openedx/paragon';
import {
  Edit,
  Check,
  Close,
  Add,
  MoreHoriz,
} from '@openedx/paragon/icons';
import { getAuthenticatedUser, getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';
import { getConfig } from '@edx/frontend-platform';
import { useDispatch, useSelector } from 'react-redux';
import { logStudioHomeError } from '../../../logging';

import {
  getSavingStatuses, getCompanyCatalog, getCompanyAdmins, getCompanyEmployees,
} from '../../../data/selectors';
import LearnerSearchDropdown, { LearnerOption } from './LearnerSearchDropdown';
import {
  updateCompanyField,
  updateCustomCsvFields,
  updateCustomAdminPortalFeatures,
  manageCompanyAdmins,
  fetchCompanyCatalog,
} from '../../../data/thunks';

interface Company {
  company_name: string;
  friendly_name: string;
  edx_course_org?: string;
  size: string;
  website?: string;
  admins: string[];
  primary_admin_email: string;
  custom_csv_fields: string[];
  custom_admin_portal: string[];
}

interface CompanyDetailsProps {
  company: Company | null;
  isLoading?: boolean;
}

// Company size options as per requirements
const COMPANY_SIZES = [
  'Less than 999',
  '1000 - 4999',
  '5000 - 9999',
  '10000+',
];

// Available custom admin portal features
const ADMIN_PORTAL_FEATURES = [
  'custom-assessment',
  'reflections',
];

const CompanyDetails: React.FC<CompanyDetailsProps> = ({
  company,
  isLoading = false,
}) => {
  const dispatch = useDispatch();
  const savingStates = useSelector(getSavingStatuses);
  const companyCatalog = useSelector(getCompanyCatalog);
  const companyAdmins = useSelector(getCompanyAdmins);
  const companyEmployees = useSelector(getCompanyEmployees);

  const nonAdminEmployees = useMemo<LearnerOption[]>(() => {
    const seen = new Set<string>();
    return (Array.isArray(companyEmployees) ? companyEmployees : [])
      .filter((e: any) => {
        if (e.is_admin || !e.username || seen.has(e.username)) { return false; }
        seen.add(e.username);
        return true;
      })
      .map((e: any) => ({ name: e.name || '', email: e.email || '', username: e.username }));
  }, [companyEmployees]);

  // Get edx_course_org from company catalog data, fallback to company_name if catalog doesn't exist
  const edxCourseOrg = companyCatalog?.company?.edx_course_org || company?.company_name;

  // Check if company has been migrated to new catalog system (catalog will be empty {} if not migrated)
  const hasCatalog = companyCatalog && Object.keys(companyCatalog).length > 0;

  const authenticatedUser = getAuthenticatedUser();

  const config = getConfig();

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!authenticatedUser?.email) { return; }

      try {
        await getAuthenticatedHttpClient().get(
          `${config.LMS_BASE_URL}/ibl/user-profile?email=${authenticatedUser.email}`,
        );
      } catch (error) {
        logStudioHomeError('cmp_user_profile_fetch_failed', error);
      }
    };

    fetchUserProfile();
  }, [authenticatedUser?.email, config.LMS_BASE_URL]);

  const getAdminHubUrl = (companySlug: string) => {
    const baseUrl = (config.LEARNER_DASHBOARD_URL || '').replace(/\/learner-dashboard\/?$/, '');
    return `${baseUrl}/admin-hub/?company=${companySlug}`;
  };

  // Edit states
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, any>>({});

  // Pending state for catalog-related fields (buffered until Submit is clicked)
  const [pendingSkillSelect, setPendingSkillSelect] = useState<any>(null);
  const [pendingOptions, setPendingOptions] = useState<any>(null);
  const [pendingAdminPortalFeatures, setPendingAdminPortalFeatures] = useState<string[] | null>(null);

  // String state for skill select number inputs (allows free typing)
  const [pendingLimitValue, setPendingLimitValue] = useState<string>('');
  const [pendingTotalAvailableValue, setPendingTotalAvailableValue] = useState<string>('');

  // Validation errors (checked at submit time / on input change)
  const [limitValidationError, setLimitValidationError] = useState<string | null>(null);
  const [totalAvailableValidationError, setTotalAvailableValidationError] = useState<string | null>(null);

  // Saving states for the three catalog sections
  const [isSavingSkillSelect, setIsSavingSkillSelect] = useState(false);
  const [isSavingOptions, setIsSavingOptions] = useState(false);
  const [isSavingCustomFeatures, setIsSavingCustomFeatures] = useState(false);

  // Edit mode for each catalog section (pencil icon toggles these)
  const [isEditingSkillSelect, setIsEditingSkillSelect] = useState(false);
  const [isEditingOptions, setIsEditingOptions] = useState(false);
  const [isEditingCustomFeatures, setIsEditingCustomFeatures] = useState(false);

  // Sync pending state when Redux catalog state changes (e.g. after initial load or after a save)
  React.useEffect(() => {
    if (companyCatalog?.skill_select) {
      setPendingSkillSelect(companyCatalog.skill_select);
      setPendingLimitValue(
        companyCatalog.skill_select.limit === null
          ? ''
          : String(companyCatalog.skill_select.limit ?? ''),
      );
      setPendingTotalAvailableValue(
        companyCatalog.skill_select.total_available === null
          ? ''
          : String(companyCatalog.skill_select.total_available ?? ''),
      );
    }
    if (companyCatalog?.options) {
      setPendingOptions(companyCatalog.options);
    }
  }, [companyCatalog]);

  // Fetch company catalog data when company changes to get edx_course_org
  React.useEffect(() => {
    if (company?.company_name) {
      dispatch(fetchCompanyCatalog(company.company_name));
    }
  }, [company?.company_name, dispatch]);

  // Sync pending admin portal features when company changes
  React.useEffect(() => {
    if (company?.custom_admin_portal) {
      setPendingAdminPortalFeatures([...company.custom_admin_portal]);
    }
  }, [company?.company_name]);

  // Modal states
  const [isAddAdminModalOpen, openAddAdminModal, closeAddAdminModal] = useToggle(false);

  // CSV fields inline edit state
  const [isEditingCsvFields, setIsEditingCsvFields] = useState(false);
  const [pendingCsvFields, setPendingCsvFields] = useState<string[]>([]);
  const [newCsvInputs, setNewCsvInputs] = useState<string[]>(['']);
  const [csvValidationError, setCsvValidationError] = useState<string | null>(null);
  const [adminForm, setAdminForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    jobTitle: '',
    password: '',
  });
  const [isPromoteAdminModalOpen, setIsPromoteAdminModalOpen] = useState(false);
  const [promoteSearchValue, setPromoteSearchValue] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<LearnerOption | null>(null);
  const [isPromoteFocused, setIsPromoteFocused] = useState(false);

  const isSaving = (field: string) => {
    const savingKey = `update${field.charAt(0).toUpperCase() + field.slice(1)}SavingStatus`;
    return savingStates[savingKey] === 'pending';
  };

  const startEditing = (field: string, currentValue: any) => {
    setEditingField(field);
    setEditValues({ ...editValues, [field]: currentValue });
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValues({});
  };

  const saveField = async (field: string) => {
    if (!company) { return; }

    try {
      await dispatch(updateCompanyField(company.company_name, field, editValues[field]));
      setEditingField(null);
      setEditValues({});
      // Redux state will update automatically via fetchCompanyManagementSpecificDataSuccess
    } catch (error) {
      logStudioHomeError('cmp_company_field_update_failed', error, { field });
    }
  };

  const startEditingCsvFields = () => {
    if (!company) { return; }
    setPendingCsvFields([...company.custom_csv_fields]);
    setNewCsvInputs(['']);
    setCsvValidationError(null);
    setIsEditingCsvFields(true);
  };

  const handleCsvFieldRemovePending = (fieldToRemove: string) => {
    setPendingCsvFields(prev => prev.filter(f => f !== fieldToRemove));
  };

  const handleNewCsvInputChange = (index: number, value: string) => {
    // Strip all whitespace characters as the user types
    const stripped = value.replace(/\s/g, '');
    setNewCsvInputs(prev => prev.map((v, i) => (i === index ? stripped : v)));
    setCsvValidationError(null);
  };

  const handleAddCsvInputRow = () => {
    setNewCsvInputs(prev => [...prev, '']);
  };

  const handleRemoveCsvInputRow = (index: number) => {
    setNewCsvInputs(prev => prev.filter((_, i) => i !== index));
  };

  const handleCsvFieldsSave = async () => {
    if (!company) { return; }

    // Collect non-empty new inputs, already stripped of whitespace
    const toAdd = newCsvInputs
      .map(v => v.trim())
      .filter(v => v.length > 0);

    // Check for duplicates against existing pending fields
    const allExisting = new Set(pendingCsvFields.map(f => f.toLowerCase()));
    const duplicates = toAdd.filter(f => allExisting.has(f.toLowerCase()));
    if (duplicates.length > 0) {
      setCsvValidationError(`Duplicate field(s): ${duplicates.join(', ')}`);
      return;
    }

    setCsvValidationError(null);

    const original = company.custom_csv_fields;
    const toRemove = original.filter(f => !pendingCsvFields.includes(f));

    try {
      if (toRemove.length > 0) {
        await dispatch(updateCustomCsvFields(company.company_name, 'remove', toRemove));
      }
      if (toAdd.length > 0) {
        await dispatch(updateCustomCsvFields(company.company_name, 'add', toAdd));
      }
      setIsEditingCsvFields(false);
    } catch (error) {
      logStudioHomeError('cmp_csv_fields_update_failed', error, {
        addedCount: toAdd.length,
        removedCount: toRemove.length,
      });
    }
  };

  const handleCsvFieldsCancel = () => {
    setIsEditingCsvFields(false);
    setNewCsvInputs(['']);
    setCsvValidationError(null);
  };

  const handleAdminPortalFeatureToggle = (feature: string) => {
    setPendingAdminPortalFeatures((prev) => {
      const current = prev ?? company?.custom_admin_portal ?? [];
      return current.includes(feature)
        ? current.filter((f) => f !== feature)
        : [...current, feature];
    });
  };

  const handleSubmitCustomFeatures = async () => {
    if (!company) { return; }
    const current = company.custom_admin_portal ?? [];
    const pending = pendingAdminPortalFeatures ?? current;

    const toAdd = pending.filter((f) => !current.includes(f));
    const toRemove = current.filter((f) => !pending.includes(f));

    setIsSavingCustomFeatures(true);
    try {
      if (toAdd.length > 0) {
        await dispatch(updateCustomAdminPortalFeatures(company.company_name, 'add', toAdd));
      }
      if (toRemove.length > 0) {
        await dispatch(updateCustomAdminPortalFeatures(company.company_name, 'remove', toRemove));
      }
      setIsEditingCustomFeatures(false);
    } catch (error) {
      // error handling could be added here if needed
    } finally {
      setIsSavingCustomFeatures(false);
    }
  };

  const handleCancelCustomFeatures = () => {
    setPendingAdminPortalFeatures(company ? [...company.custom_admin_portal] : []);
    setIsEditingCustomFeatures(false);
  };

  const handleSkillSelectToggle = (checked: boolean) => {
    setPendingSkillSelect((prev: any) => ({
      ...(prev ?? companyCatalog?.skill_select ?? {}),
      enabled: checked,
    }));
  };

  /**
   * Shared helper: PATCH the skill-select endpoint then refresh Redux catalog.
   */
  const patchSkillSelect = useCallback(async (updatedSkillSelect: object) => {
    if (!company) { return; }
    await getAuthenticatedHttpClient().patch(
      `${config.LMS_BASE_URL}/api/ibl/dl_enterprise_app/enterprise/company-catalog/skill-select`,
      { company_name: company.company_name, skill_select: updatedSkillSelect },
    );
    dispatch(fetchCompanyCatalog(company.company_name));
  }, [company?.company_name, dispatch]);

  const handleSubmitSkillSelect = async () => {
    if (!company || !pendingSkillSelect) { return; }

    // Final validation before submit
    const limit = pendingLimitValue.trim() === '' ? null : parseInt(pendingLimitValue, 10);
    const totalAvailable = pendingTotalAvailableValue.trim() === '' ? null : parseInt(pendingTotalAvailableValue, 10);

    if (limit !== null && Number.isNaN(limit)) {
      setLimitValidationError('Limit must be a valid number.');
      return;
    }
    if (totalAvailable !== null && Number.isNaN(totalAvailable)) {
      setTotalAvailableValidationError('Total Available must be a valid number.');
      return;
    }
    if (limit !== null && totalAvailable !== null && limit > totalAvailable) {
      setLimitValidationError(`Limit per user (${limit}) cannot exceed Total Available (${totalAvailable})`);
      return;
    }

    setLimitValidationError(null);
    setTotalAvailableValidationError(null);
    setIsSavingSkillSelect(true);
    try {
      await patchSkillSelect({
        enabled: pendingSkillSelect.enabled ?? false,
        limit: pendingSkillSelect.limit === null ? null : limit,
        total_available: pendingSkillSelect.total_available === null ? null : totalAvailable,
      });
      setIsEditingSkillSelect(false);
    } catch (error) {
      // error handling could be added here if needed
    } finally {
      setIsSavingSkillSelect(false);
    }
  };

  const handleCancelSkillSelect = () => {
    const saved = companyCatalog?.skill_select ?? null;
    setPendingSkillSelect(saved);
    setPendingLimitValue(saved?.limit === null ? '' : String(saved?.limit ?? ''));
    setPendingTotalAvailableValue(saved?.total_available === null ? '' : String(saved?.total_available ?? ''));
    setLimitValidationError(null);
    setTotalAvailableValidationError(null);
    setIsEditingSkillSelect(false);
  };

  const handleDisplayOptionToggle = (feature: string, checked: boolean) => {
    setPendingOptions((prev: any) => {
      const current = prev ?? companyCatalog?.options ?? {};
      return {
        hide_skills_if_no_licenses: current.hide_skills_if_no_licenses ?? false,
        hide_skills_if_only_pathways: current.hide_skills_if_only_pathways ?? false,
        hide_pathways_if_only_skills: current.hide_pathways_if_only_skills ?? false,
        [feature]: checked,
      };
    });
  };

  const handleSubmitDisplayOptions = async () => {
    if (!company || !pendingOptions) { return; }
    setIsSavingOptions(true);
    try {
      await getAuthenticatedHttpClient().patch(
        `${config.LMS_BASE_URL}/api/ibl/dl_enterprise_app/enterprise/company-catalog/options`,
        { company_name: company.company_name, options: pendingOptions },
      );
      await dispatch(fetchCompanyCatalog(company.company_name));
      setIsEditingOptions(false);
    } catch (error) {
      // Revert on error
      setPendingOptions(companyCatalog?.options ?? null);
    } finally {
      setIsSavingOptions(false);
    }
  };

  const handleCancelDisplayOptions = () => {
    setPendingOptions(companyCatalog?.options ?? null);
    setIsEditingOptions(false);
  };

  const generateRandomPassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handlePromoteToAdmin = async () => {
    if (!company || !selectedEmployee) { return; }

    try {
      await dispatch(manageCompanyAdmins(company.company_name, 'add', {
        email: selectedEmployee.email,
        newUser: false,
      }));
      setPromoteSearchValue('');
      setSelectedEmployee(null);
      setIsPromoteAdminModalOpen(false);
    } catch (error) {
      logStudioHomeError('cmp_admin_promotion_failed', error);
    }
  };

  const handleAdminAdd = async () => {
    if (!company || !adminForm.email) { return; }

    try {
      const adminData = {
        email: adminForm.email,
        firstName: adminForm.firstName || 'New',
        lastName: adminForm.lastName || 'Admin',
        jobTitle: adminForm.jobTitle || 'Administrator',
        password: adminForm.password || generateRandomPassword(),
        companyFriendlyName: company.friendly_name,
        newUser: true,
        metadata: {},
        is_company_catalog: hasCatalog,
        must_update_pass: true,
      };

      await dispatch(manageCompanyAdmins(company.company_name, 'add', adminData));
      setAdminForm({
        email: '', firstName: '', lastName: '', jobTitle: '', password: '',
      });
      closeAddAdminModal();
      // Redux state will update automatically via fetchCompanyManagementSpecificDataSuccess
    } catch (error) {
      logStudioHomeError('cmp_admin_add_failed', error);
    }
  };

  const handleAdminRemove = async (adminEmail: string, removeCompletely: boolean = false) => {
    if (!company) { return; }

    try {
      await dispatch(manageCompanyAdmins(company.company_name, 'remove', {
        email: adminEmail,
        removeCompletely,
      }));
      // Redux state will update automatically via fetchCompanyManagementSpecificDataSuccess
    } catch (error) {
      logStudioHomeError('cmp_admin_remove_failed', error);
    }
  };

  const handleSetPrimaryAdmin = async (adminEmail: string) => {
    if (!company) { return; }

    try {
      await dispatch(manageCompanyAdmins(company.company_name, 'setPrimary', {
        email: adminEmail,
      }));
      // Redux state will update automatically via fetchCompanyManagementSpecificDataSuccess
    } catch (error) {
      logStudioHomeError('cmp_primary_admin_update_failed', error);
    }
  };

  if (isLoading) {
    return (
      <Card className="company-details-card">
        <Card.Body>
          <Alert variant="info">Loading company details...</Alert>
        </Card.Body>
      </Card>
    );
  }

  if (!company) {
    return (
      <Card className="company-details-card">
        <Card.Body>
          <Alert variant="warning">Please select a company to view and edit details.</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="company-details-card">
      <Card.Body>
        {/* Company Name/Friendly Name - Editable */}
        <Row className="mb-4">
          <Col md={4}>
            <div className="detail-group">
              <div className="d-flex align-items-center">
                <strong>Company Name:</strong>
                {editingField !== 'friendlyName' && (
                  <IconButton
                    src={Edit}
                    iconAs={Edit}
                    alt="Edit company name"
                    onClick={() => startEditing('friendlyName', company.friendly_name)}
                    variant="primary"
                    size="sm"
                    className="ms-2"
                  />
                )}
              </div>
              {editingField === 'friendlyName' ? (
                <div className="mt-2">
                  <Form.Group>
                    <Form.Control
                      type="text"
                      value={editValues.friendlyName || ''}
                      onChange={(e) => setEditValues({ ...editValues, friendlyName: e.target.value })}
                      disabled={isSaving('companyFriendlyName')}
                    />
                  </Form.Group>
                  <ActionRow className="mt-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => saveField('friendlyName')}
                      disabled={isSaving('companyFriendlyName')}
                    >
                      <Check className="me-1" />
                      {isSaving('companyFriendlyName') ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={cancelEditing}
                      disabled={isSaving('companyFriendlyName')}
                    >
                      <Close className="me-1" />
                      Cancel
                    </Button>
                  </ActionRow>
                </div>
              ) : (
                <div className="detail-value">
                  <span>{company.friendly_name}</span>
                </div>
              )}
            </div>
          </Col>
          <Col md={4}>
            <div className="detail-group">
              <strong>Company Code:</strong>
              <div className="detail-value">
                <Badge variant="secondary">{company.company_name}</Badge>
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div className="detail-group">
              <div className="d-flex align-items-center">
                <strong>Course Authoring Organization:</strong>
                {hasCatalog && editingField !== 'edxCourseOrg' && (
                  <IconButton
                    src={Edit}
                    iconAs={Edit}
                    alt="Edit course authoring organization"
                    onClick={() => startEditing('edxCourseOrg', edxCourseOrg || '')}
                    variant="primary"
                    size="sm"
                    className="ms-2"
                  />
                )}
              </div>
              {editingField === 'edxCourseOrg' ? (
                <div className="mt-2">
                  <Form.Group>
                    <Form.Control
                      type="text"
                      placeholder="Enter organization"
                      value={editValues.edxCourseOrg || ''}
                      onChange={(e) => setEditValues({ ...editValues, edxCourseOrg: e.target.value })}
                      disabled={isSaving('companyEdxCourseOrg')}
                    />
                  </Form.Group>
                  <ActionRow className="mt-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => saveField('edxCourseOrg')}
                      disabled={isSaving('companyEdxCourseOrg')}
                    >
                      <Check className="me-1" />
                      {isSaving('companyEdxCourseOrg') ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={cancelEditing}
                      disabled={isSaving('companyEdxCourseOrg')}
                    >
                      <Close className="me-1" />
                      Cancel
                    </Button>
                  </ActionRow>
                </div>
              ) : (
                <div className="detail-value">
                  {edxCourseOrg ? (
                    <span>{edxCourseOrg}</span>
                  ) : (
                    <span className="text-muted">Not specified</span>
                  )}
                  {!hasCatalog && edxCourseOrg && (
                    <small className="text-muted d-block mt-1">Read-only: migrate to Company Catalog to edit</small>
                  )}
                </div>
              )}
            </div>
          </Col>
        </Row>

        <Row className="mb-4">
          {/* Company Size - Editable */}
          <Col md={4}>
            <div className="detail-group">
              <div className="d-flex align-items-center">
                <strong>Company Size:</strong>
                {editingField !== 'size' && (
                  <IconButton
                    src={Edit}
                    iconAs={Edit}
                    alt="Edit company size"
                    onClick={() => startEditing('size', company.size)}
                    variant="primary"
                    size="sm"
                    className="ms-2"
                  />
                )}
              </div>
              {editingField === 'size' ? (
                <div className="mt-2">
                  <Form.Group>
                    <Form.Control
                      as="select"
                      value={editValues.size || ''}
                      onChange={(e) => setEditValues({ ...editValues, size: e.target.value })}
                      disabled={isSaving('companySize')}
                    >
                      {COMPANY_SIZES.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <ActionRow className="mt-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => saveField('size')}
                      disabled={isSaving('companySize')}
                    >
                      <Check className="me-1" />
                      {isSaving('companySize') ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={cancelEditing}
                      disabled={isSaving('companySize')}
                    >
                      <Close className="me-1" />
                      Cancel
                    </Button>
                  </ActionRow>
                </div>
              ) : (
                <div className="detail-value">
                  <span>{company.size}</span>
                </div>
              )}
            </div>
          </Col>

          {/* Website - Editable */}
          <Col md={8}>
            <div className="detail-group">
              <div className="d-flex align-items-center">
                <strong>Website:</strong>
                {editingField !== 'website' && (
                  <IconButton
                    src={Edit}
                    iconAs={Edit}
                    alt="Edit website"
                    onClick={() => startEditing('website', company.website || '')}
                    variant="primary"
                    size="sm"
                    className="ms-2"
                  />
                )}
              </div>
              {editingField === 'website' ? (
                <div className="mt-2">
                  <Form.Group>
                    <Form.Control
                      type="text"
                      placeholder="www.example.com"
                      value={editValues.website || ''}
                      onChange={(e) => setEditValues({ ...editValues, website: e.target.value })}
                      disabled={isSaving('companyWebsite')}
                    />
                  </Form.Group>
                  <ActionRow className="mt-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => saveField('website')}
                      disabled={isSaving('companyWebsite')}
                    >
                      <Check className="me-1" />
                      {isSaving('companyWebsite') ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={cancelEditing}
                      disabled={isSaving('companyWebsite')}
                    >
                      <Close className="me-1" />
                      Cancel
                    </Button>
                  </ActionRow>
                </div>
              ) : (
                <div className="detail-value">
                  {company.website ? (
                    <a
                      href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary"
                    >
                      {company.website}
                    </a>
                  ) : (
                    <span className="text-muted">Not specified</span>
                  )}
                </div>
              )}

              {authenticatedUser?.administrator && (
                <div className="mt-2">
                  <a
                    href={getAdminHubUrl(company.company_name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary"
                  >
                    Go to {company.friendly_name}&apos;s Admin Hub →
                  </a>
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Catalog Display Options & Custom Admin Portal Features - Editable */}
        <Row className="mb-4">
          <Col xs={6}>
            <div className="detail-group">
              <div className="d-flex align-items-center">
                <strong>Catalog Display Options:</strong>
                {hasCatalog && !isEditingOptions && (
                  <IconButton
                    src={Edit}
                    iconAs={Edit}
                    alt="Edit catalog display options"
                    onClick={() => setIsEditingOptions(true)}
                    variant="primary"
                    size="sm"
                    className="ms-2"
                  />
                )}
              </div>
              <div className="detail-value mt-2">
                {hasCatalog ? (
                  <>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="checkbox"
                        label="Hide skills if no licenses"
                        checked={pendingOptions?.hide_skills_if_no_licenses || false}
                        disabled={!isEditingOptions}
                        onChange={(e) => handleDisplayOptionToggle('hide_skills_if_no_licenses', e.target.checked)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="checkbox"
                        label="Hide skills if only pathways"
                        checked={pendingOptions?.hide_skills_if_only_pathways || false}
                        disabled={!isEditingOptions}
                        onChange={(e) => handleDisplayOptionToggle('hide_skills_if_only_pathways', e.target.checked)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="checkbox"
                        label="Hide pathways if only skills"
                        checked={pendingOptions?.hide_pathways_if_only_skills || false}
                        disabled={!isEditingOptions}
                        onChange={(e) => handleDisplayOptionToggle('hide_pathways_if_only_skills', e.target.checked)}
                      />
                    </Form.Group>
                    {isEditingOptions && (
                      <ActionRow className="mt-3">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleSubmitDisplayOptions}
                          disabled={isSavingOptions}
                        >
                          <Check className="me-1" />
                          {isSavingOptions ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          variant="tertiary"
                          size="sm"
                          onClick={handleCancelDisplayOptions}
                          disabled={isSavingOptions}
                        >
                          <Close className="me-1" />
                          Cancel
                        </Button>
                      </ActionRow>
                    )}
                  </>
                ) : (
                  <small className="text-muted">No catalog</small>
                )}
              </div>
            </div>
          </Col>
          <Col xs={6}>
            <div className="detail-group">
              <div className="d-flex align-items-center">
                <strong>Custom Features:</strong>
                {!isEditingCustomFeatures && (
                  <IconButton
                    src={Edit}
                    iconAs={Edit}
                    alt="Edit custom features"
                    onClick={() => setIsEditingCustomFeatures(true)}
                    variant="primary"
                    size="sm"
                    className="ms-2"
                  />
                )}
              </div>
              <div className="detail-value mt-2">
                {ADMIN_PORTAL_FEATURES.map(feature => {
                  const isEnabled = (pendingAdminPortalFeatures ?? company.custom_admin_portal).includes(feature);
                  return (
                    <Form.Group key={feature} className="mb-2">
                      <Form.Check
                        type="checkbox"
                        label={feature}
                        checked={isEnabled}
                        disabled={!isEditingCustomFeatures || isSavingCustomFeatures}
                        onChange={() => handleAdminPortalFeatureToggle(feature)}
                      />
                    </Form.Group>
                  );
                })}
                {isEditingCustomFeatures && (
                  <ActionRow className="mt-3">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSubmitCustomFeatures}
                      disabled={isSavingCustomFeatures}
                    >
                      <Check className="me-1" />
                      {isSavingCustomFeatures ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={handleCancelCustomFeatures}
                      disabled={isSavingCustomFeatures}
                    >
                      <Close className="me-1" />
                      Cancel
                    </Button>
                  </ActionRow>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* Skill Select Configuration - Editable */}
        <Row className="mb-4">
          <Col xs={12}>
            <div className="detail-group">
              <div className="d-flex align-items-center">
                <strong>Skill Select Configuration:</strong>
                {hasCatalog && !isEditingSkillSelect && (
                  <IconButton
                    src={Edit}
                    iconAs={Edit}
                    alt="Edit skill select configuration"
                    onClick={() => setIsEditingSkillSelect(true)}
                    variant="primary"
                    size="sm"
                    className="ms-2"
                  />
                )}
              </div>
              {!hasCatalog && (
                <Alert variant="warning" className="mt-2">
                  Please migrate {company.company_name} to Company Catalog
                </Alert>
              )}
              <div className="detail-value mt-2">
                <Form.Group className="mb-2">
                  <Form.Check
                    type="checkbox"
                    label="Skill Select Enabled"
                    checked={pendingSkillSelect?.enabled || false}
                    disabled={!hasCatalog || !isEditingSkillSelect}
                    onChange={(e) => handleSkillSelectToggle(e.target.checked)}
                  />
                </Form.Group>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">
                        Limit (per user)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        min="1"
                        value={pendingLimitValue}
                        disabled={!hasCatalog || !isEditingSkillSelect || pendingSkillSelect?.limit === null}
                        onChange={(e) => {
                          const { value } = e.target;
                          setPendingLimitValue(value);
                          setLimitValidationError(null);
                        }}
                      />
                      {limitValidationError && (
                      <Alert variant="danger" className="mt-2 mb-2" dismissible onClose={() => setLimitValidationError(null)}>
                        {limitValidationError}
                      </Alert>
                      )}
                      <Form.Text className="d-block mb-2">Max skills per user</Form.Text>
                      <Form.Check
                        type="checkbox"
                        label="Unlimited"
                        checked={pendingSkillSelect?.limit === null}
                        disabled={!hasCatalog || !isEditingSkillSelect}
                        onChange={(e) => {
                          const isUnlimited = e.target.checked;
                          let newLimit = null;
                          if (!isUnlimited) {
                            newLimit = pendingSkillSelect?.limit !== null
                               && pendingSkillSelect?.limit !== undefined
                              ? pendingSkillSelect.limit
                              : 1;
                          }
                          setPendingLimitValue(newLimit === null ? '' : String(newLimit));
                          setLimitValidationError(null);
                          setPendingSkillSelect((prev: any) => ({
                            ...(prev ?? companyCatalog?.skill_select ?? {}),
                            limit: newLimit,
                          }));
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">
                        Total Available (per company)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        min="0"
                        value={pendingTotalAvailableValue}
                        disabled={!hasCatalog || !isEditingSkillSelect || pendingSkillSelect?.total_available === null}
                        onChange={(e) => {
                          const { value } = e.target;
                          setPendingTotalAvailableValue(value);
                          setTotalAvailableValidationError(null);
                        }}
                      />
                      {totalAvailableValidationError && (
                      <Alert variant="danger" className="mt-2 mb-2" dismissible onClose={() => setTotalAvailableValidationError(null)}>
                        {totalAvailableValidationError}
                      </Alert>
                      )}
                      <Form.Text className="d-block mb-2">Total company licenses</Form.Text>
                      <Form.Check
                        type="checkbox"
                        label="Unlimited"
                        checked={pendingSkillSelect?.total_available === null}
                        disabled={!hasCatalog || !isEditingSkillSelect}
                        onChange={(e) => {
                          const isUnlimited = e.target.checked;
                          let newTotalAvailable = null;
                          if (!isUnlimited) {
                            newTotalAvailable = pendingSkillSelect?.total_available !== null
                               && pendingSkillSelect?.total_available !== undefined
                              ? pendingSkillSelect.total_available
                              : 0;
                          }
                          setPendingTotalAvailableValue(newTotalAvailable === null ? '' : String(newTotalAvailable));
                          setTotalAvailableValidationError(null);
                          setPendingSkillSelect((prev: any) => ({
                            ...(prev ?? companyCatalog?.skill_select ?? {}),
                            total_available: newTotalAvailable,
                          }));
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Total Used</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={companyCatalog?.totals?.used || 0}
                        disabled
                        readOnly
                      />
                      <Form.Text>
                        Read-only: calculated from enrollments
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                {isEditingSkillSelect && (
                <ActionRow className="mt-3">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmitSkillSelect}
                    disabled={isSavingSkillSelect}
                  >
                    <Check className="me-1" />
                    {isSavingSkillSelect ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={handleCancelSkillSelect}
                    disabled={isSavingSkillSelect}
                  >
                    <Close className="me-1" />
                    Cancel
                  </Button>
                </ActionRow>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* Custom CSV Fields - Editable */}
        <Row className="mb-4">
          <Col xs={12}>
            <div className="detail-group">
              <div className="d-flex align-items-center">
                <strong>Custom CSV Fields ({company.custom_csv_fields.length}):</strong>
                {!isEditingCsvFields && (
                  <IconButton
                    src={Edit}
                    iconAs={Edit}
                    alt="Edit custom CSV fields"
                    onClick={startEditingCsvFields}
                    variant="primary"
                    size="sm"
                    className="ms-2"
                  />
                )}
              </div>

              {isEditingCsvFields ? (
                <div className="mt-2">
                  {/* Existing fields as removable badges */}
                  {pendingCsvFields.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {pendingCsvFields.map((field) => (
                        <Badge
                          key={field}
                          variant="secondary"
                          className="d-flex align-items-center mr-2 mb-2"
                        >
                          {field}
                          <IconButton
                            src={Close}
                            iconAs={Close}
                            alt={`Remove ${field}`}
                            onClick={() => handleCsvFieldRemovePending(field)}
                            variant="light"
                            size="sm"
                            className="ms-1"
                          />
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* New field inputs */}
                  <Form.Label className="small"><strong>Add new fields:</strong></Form.Label>
                  {newCsvInputs.map((value, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={index} className="d-flex align-items-center mb-2" style={{ gap: '0.5rem' }}>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={value}
                        placeholder="field_name (no spaces)"
                        onChange={(e) => handleNewCsvInputChange(index, e.target.value)}
                        style={{ maxWidth: '300px' }}
                      />
                      {newCsvInputs.length > 1 && (
                      <IconButton
                        src={Close}
                        iconAs={Close}
                        alt="Remove this input"
                        onClick={() => handleRemoveCsvInputRow(index)}
                        variant="primary"
                        size="sm"
                      />
                      )}
                    </div>
                  ))}
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleAddCsvInputRow}
                    className="p-0 mb-3"
                  >
                    <Add className="me-1" />
                    Add another field
                  </Button>

                  {csvValidationError && (
                    <Alert variant="danger" className="mt-2 mb-2" dismissible onClose={() => setCsvValidationError(null)}>
                      {csvValidationError}
                    </Alert>
                  )}

                  <ActionRow className="mt-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleCsvFieldsSave}
                      disabled={savingStates.updateCustomCsvFieldsSavingStatus === 'pending'}
                    >
                      <Check className="me-1" />
                      {savingStates.updateCustomCsvFieldsSavingStatus === 'pending' ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={handleCsvFieldsCancel}
                      disabled={savingStates.updateCustomCsvFieldsSavingStatus === 'pending'}
                    >
                      <Close className="me-1" />
                      Cancel
                    </Button>
                  </ActionRow>
                </div>
              ) : (
                <div className="detail-value mt-2">
                  {company.custom_csv_fields.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {company.custom_csv_fields.map((field) => (
                        <Badge
                          key={field}
                          variant="secondary"
                          className="mr-2 mb-2"
                        >
                          {field}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted">No custom CSV fields</span>
                  )}
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Admins Management - Editable */}
        <Row className="mb-4">
          <Col xs={12}>
            <div className="detail-group">
              <div className="d-flex justify-content-between align-items-center">
                <strong>Admins ({companyAdmins.length}):</strong>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setIsPromoteAdminModalOpen(true)}
                    disabled={nonAdminEmployees.length === 0 || savingStates.manageCompanyAdminsSavingStatus === 'pending'}
                  >
                    Promote To Admin
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={openAddAdminModal}
                    disabled={savingStates.manageCompanyAdminsSavingStatus === 'pending'}
                  >
                    <Add className="me-1" />
                    Invite New Admin
                  </Button>
                </div>
              </div>
              <div className="detail-value mt-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {companyAdmins.length > 0 ? (
                  <div className="admin-list">
                    {companyAdmins.map((admin) => {
                      const isPrimary = admin.is_primary_admin;
                      return (
                        <div key={admin.email} className="admin-item d-flex justify-content-between align-items-center mb-2 p-2 border rounded">
                          <div>
                            <Badge
                              variant={isPrimary ? 'primary' : 'info'}
                              className="me-2"
                            >
                              {admin.email}
                            </Badge>
                            {isPrimary && <small className="text-muted">(Primary Admin)</small>}
                          </div>
                          <Dropdown>
                            <Dropdown.Toggle
                              as={Button}
                              variant="light"
                              size="sm"
                              disabled={savingStates.manageCompanyAdminsSavingStatus === 'pending'}
                              className="dropdown-toggle-split"
                            >
                              <MoreHoriz />
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                              {!isPrimary && (
                              <Dropdown.Item
                                onClick={() => handleSetPrimaryAdmin(admin.email)}
                              >
                                Set as Primary Admin
                              </Dropdown.Item>
                              )}
                              {!isPrimary && (
                              <>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                  onClick={() => handleAdminRemove(admin.email, false)}
                                >
                                  Remove Admin Status
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleAdminRemove(admin.email, true)}
                                  className="text-danger"
                                >
                                  Remove Completely
                                </Dropdown.Item>
                              </>
                              )}
                              {isPrimary && (
                              <Dropdown.Item disabled>
                                <small className="text-muted">Primary admin cannot be removed</small>
                              </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span className="text-muted">No admins specified</span>
                )}
                {savingStates.manageCompanyAdminsSavingStatus === 'pending' && (
                  <div className="mt-2">
                    <small className="text-muted">Managing admins...</small>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* Promote To Admin Modal */}
        <ModalDialog
          title="Promote To Admin"
          isOpen={isPromoteAdminModalOpen}
          onClose={() => {
            setIsPromoteAdminModalOpen(false);
            setPromoteSearchValue('');
            setSelectedEmployee(null);
          }}
          hasCloseButton
          isFullscreenOnMobile
        >
          <ModalDialog.Header>
            <ModalDialog.Title>Promote To Admin</ModalDialog.Title>
          </ModalDialog.Header>
          <ModalDialog.Body>
            <Form.Group>
              <Form.Label>Select Employee</Form.Label>
              <div style={{ marginBottom: isPromoteFocused ? '14rem' : undefined }}>
                <LearnerSearchDropdown
                  learners={nonAdminEmployees}
                  value={promoteSearchValue}
                  onChange={setPromoteSearchValue}
                  onSelect={(learner) => setSelectedEmployee(learner)}
                  onFocus={() => setIsPromoteFocused(true)}
                  onBlur={() => setIsPromoteFocused(false)}
                  placeholder="Search by name or email..."
                  disabled={savingStates.manageCompanyAdminsSavingStatus === 'pending'}
                  id="promote-admin-search"
                />
              </div>
            </Form.Group>
          </ModalDialog.Body>
          <ModalDialog.Footer>
            <ActionRow>
              <ModalDialog.CloseButton variant="tertiary">
                Cancel
              </ModalDialog.CloseButton>
              <Button
                variant="primary"
                onClick={handlePromoteToAdmin}
                disabled={!selectedEmployee || savingStates.manageCompanyAdminsSavingStatus === 'pending'}
              >
                {savingStates.manageCompanyAdminsSavingStatus === 'pending' ? 'Promoting...' : 'Promote To Admin'}
              </Button>
            </ActionRow>
          </ModalDialog.Footer>
        </ModalDialog>

        {/* Add Admin Modal */}
        <ModalDialog
          title="Invite New Admin"
          isOpen={isAddAdminModalOpen}
          onClose={closeAddAdminModal}
          hasCloseButton
          isFullscreenOnMobile
        >
          <ModalDialog.Header>
            <ModalDialog.Title>Invite New Admin</ModalDialog.Title>
          </ModalDialog.Header>
          <ModalDialog.Body>
            <Form.Group className="mb-3">
              <Form.Label>Email Address *</Form.Label>
              <Form.Control
                type="email"
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                placeholder="admin@company.com"
                required
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={adminForm.firstName}
                    onChange={(e) => setAdminForm({ ...adminForm, firstName: e.target.value })}
                    placeholder="John"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={adminForm.lastName}
                    onChange={(e) => setAdminForm({ ...adminForm, lastName: e.target.value })}
                    placeholder="Doe"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Job Title</Form.Label>
              <Form.Control
                type="text"
                value={adminForm.jobTitle}
                onChange={(e) => setAdminForm({ ...adminForm, jobTitle: e.target.value })}
                placeholder="Administrator"
              />
            </Form.Group>
            <Alert variant="info" className="mb-3">
              A secure password will be auto-generated by the system and emailed to the new admin.
            </Alert>
          </ModalDialog.Body>
          <ModalDialog.Footer>
            <ActionRow>
              <ModalDialog.CloseButton variant="tertiary">
                Cancel
              </ModalDialog.CloseButton>
              <Button
                variant="primary"
                onClick={handleAdminAdd}
                disabled={!adminForm.email || savingStates.manageCompanyAdminsSavingStatus === 'pending'}
              >
                {savingStates.manageCompanyAdminsSavingStatus === 'pending' ? 'Adding...' : 'Add Admin'}
              </Button>
            </ActionRow>
          </ModalDialog.Footer>
        </ModalDialog>
      </Card.Body>
    </Card>
  );
};

export default CompanyDetails;
